import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Button, Card, Empty, Flex, Spin } from "antd";
import { useHistory, useParams } from "react-router-dom";
import { FaArrowLeft, FaDownload, FaPrint } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { Error as ErrorMessage } from "../../Messages/Messages";
import { useIsMobile } from "../../../design-system/useMediaQuery";
import "./SalarySlip.css";
import { saveFile, openFile } from "../../../native/files";
import { isNative } from "../../../native/platform";

const authHeaders = () => ({ Authorization: "Bearer " + localStorage.getItem("token") });

/** File name from the server's Content-Disposition, e.g. Entgeltabrechnung_2026-09_Name.pdf */
const fileNameFrom = (headers, fallback) => {
  const match = /filename="?([^";]+)"?/i.exec(headers?.["content-disposition"] || "");
  return match ? match[1] : fallback;
};

/** With responseType "blob" an error body arrives as a Blob too, so read the message out of it. */
const errorMessageFrom = async error => {
  const data = error?.response?.data;
  if (data instanceof Blob) {
    try {
      const parsed = JSON.parse(await data.text());
      return parsed.errorMessage || parsed.error;
    } catch {
      return null;
    }
  }
  return data?.errorMessage || null;
};

/**
 * Shows the Entgeltabrechnung exactly as the server renders it. The preview, the download
 * and the printout are the same PDF, so what the employee sees can never differ from what
 * is filed.
 */
const SalarySlip = () => {
  const { t } = useTranslation("dashboard");
  const { id } = useParams();
  const history = useHistory();
  const frameRef = useRef(null);
  const [pdf, setPdf] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const isMobile = useIsMobile();

  useEffect(() => {
    let objectUrl = null;
    let cancelled = false;
    const loadPdf = async () => {
      setLoading(true);
      setFailed(false);
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/salary-slips/download/${id}?inline=1`,
          { responseType: "blob", headers: authHeaders() }
        );
        if (cancelled) return;
        const blob = new Blob([response.data], { type: "application/pdf" });
        objectUrl = URL.createObjectURL(blob);
        setPdf({
          blob,
          url: objectUrl,
          fileName: fileNameFrom(response.headers, `Entgeltabrechnung-${id}.pdf`)
        });
      } catch (error) {
        if (cancelled) return;
        setFailed(true);
        ErrorMessage((await errorMessageFrom(error)) || t("Failed to load salary slip"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    loadPdf();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [id, t]);

  const download = () => pdf && saveFile(pdf.blob, pdf.fileName);

  const print = () => {
    // App WebViews cannot print an embedded PDF; the share sheet offers Print instead.
    if (isNative) return pdf && openFile(pdf.blob, { fileName: pdf.fileName });
    const frameWindow = frameRef.current?.contentWindow;
    if (!frameWindow) return;
    frameWindow.focus();
    frameWindow.print();
  };

  // Phone browsers often cannot show a PDF inside a page (Android shows nothing, iOS only the
  // first page), so phones also get a button that opens it in the browser's own viewer.
  const openPdf = () =>
    pdf &&
    (isNative
      ? openFile(pdf.blob, { fileName: pdf.fileName })
      : window.open(pdf.url, "_blank", "noopener"));

  return (
    <Card
      className="SalarySlipDetails salary-slip-view"
      title={
        <Flex align="center" gap={8} wrap="wrap">
          <Button
            className="salary-slip-view__back"
            icon={<FaArrowLeft />}
            aria-label={t("Back")}
            onClick={() => history.goBack()}
          >
            {isMobile ? null : t("Back")}
          </Button>
          <span className="salary-slip-view__title">{t("Salary Slip")}</span>
        </Flex>
      }
      styles={{ body: { padding: 0 } }}
    >
      <Flex wrap="wrap" gap={8} justify="flex-end" className="salary-slip-view__actions">
        {isMobile && (
          <Button onClick={openPdf} disabled={!pdf}>
            {t("Open")}
          </Button>
        )}
        <Button icon={<FaPrint />} onClick={print} disabled={!pdf}>
          {t("Print")}
        </Button>
        <Button type="primary" icon={<FaDownload />} onClick={download} disabled={!pdf}>
          {t("Download PDF")}
        </Button>
      </Flex>
      {loading && (
        <Flex justify="center" align="center" className="salary-slip-view__loading">
          <Spin size="large" />
        </Flex>
      )}
      {!loading && failed && (
        <Empty className="salary-slip-view__empty" description={t("Failed to load salary slip")} />
      )}
      {!loading && pdf && (
        <iframe
          ref={frameRef}
          title={t("Salary Slip")}
          src={pdf.url}
          className="salary-slip-view__frame"
        />
      )}
    </Card>
  );
};

export default SalarySlip;
